/**
 * Modal "Editar acceso". Nombre y contacto asociado se guardan por RPC.
 * El correo solo se puede corregir en invitaciones pendientes: reemplaza la
 * invitación (nueva invitación + revocación de la anterior) y pide confirmar.
 * En accesos aceptados el correo no cambia: hay que revocar e invitar de nuevo.
 */
import { useState, useEffect } from "react";
import { X, Pencil, Loader, AlertCircle, Save } from "lucide-react";
import {
  editarAcceso, corregirCorreoInvitacion, listarContactosActivos, emailValido,
} from "../../lib/accesos";
import { mensajeError } from "../../lib/comentarios";

const C = {
  bg:"#f8fafc", white:"#ffffff", border:"#e2e8f0",
  accent:"#29abe2", accentL:"#e8f5fc",
  text:"#1e293b", sub:"#64748b", light:"#94a3b8",
  err:"#ef4444", errL:"#fee2e2", warn:"#f59e0b", warnL:"#fef3c7",
};
const iStyle = {
  width:"100%", padding:"8px 11px", borderRadius:7, border:`1px solid ${C.border}`,
  background:C.white, color:C.text, fontSize:14, fontFamily:"'DM Sans',sans-serif", boxSizing:"border-box",
};
const label = { display:"block", fontSize:12, fontWeight:600, color:C.sub, marginBottom:5 };
const btnSec = { padding:"8px 18px", borderRadius:8, border:`1px solid ${C.border}`, background:"transparent",
  color:C.sub, fontSize:14, cursor:"pointer", fontFamily:"'DM Sans',sans-serif" };

export default function EditarAccesoModal({ acceso, accesosActuales = [], onCerrar, onGuardado }) {
  const pendiente = acceso.estado === "pendiente" || acceso.estado === "vencida";
  const [nombre,     setNombre]     = useState(acceso.nombre || "");
  const [email,      setEmail]      = useState(acceso.email);
  const [contactoId, setContactoId] = useState(acceso.contacto_id || "");
  const [contactos,  setContactos]  = useState([]);
  const [confirmar,  setConfirmar]  = useState(false);
  const [guardando,  setGuardando]  = useState(false);
  const [error,      setError]      = useState("");

  useEffect(() => {
    listarContactosActivos().then(setContactos).catch(() => setContactos([]));
  }, []);

  const emailNuevo = email.trim().toLowerCase();
  const cambiaCorreo = pendiente && emailNuevo !== acceso.email;
  const repetido = cambiaCorreo && accesosActuales.some(a =>
    a.id !== acceso.id && a.proyecto_id === acceso.proyecto_id && a.email === emailNuevo && a.estado !== "revocada");
  const errNombre = !nombre.trim() ? "Falta el nombre" : "";
  const errEmail = !pendiente ? "" : !emailValido(email) ? "Correo inválido" : repetido ? "Ese correo ya tiene una invitación o acceso en este proyecto" : "";
  const hayCambios = nombre.trim() !== (acceso.nombre || "") || (contactoId || "") !== (acceso.contacto_id || "") || cambiaCorreo;
  const puedeGuardar = !guardando && !errNombre && !errEmail && hayCambios;

  const mensajeFallo = (e) => e.codigo || e.message ? e.message : mensajeError(e);

  async function guardar() {
    setGuardando(true); setError("");
    try {
      if (cambiaCorreo) {
        const r = await corregirCorreoInvitacion({ acceso, email: emailNuevo, nombre, contactoId });
        onGuardado({ tipo: r.correoEnviado ? "ok" : "error",
          texto: r.correoEnviado
            ? `Invitación reemplazada: se envió una nueva a ${emailNuevo} y el enlace enviado a ${acceso.email} dejó de funcionar.`
            : `La invitación a ${emailNuevo} quedó registrada, pero el correo no pudo enviarse. Usa “Reenviar”. El enlace anterior ya no funciona.` });
      } else {
        await editarAcceso(acceso.id, { nombre, contactoId });
        onGuardado({ tipo: "ok", texto: `Datos de ${acceso.email} actualizados.` });
      }
    } catch (e) {
      setError(mensajeFallo(e)); setConfirmar(false);
    } finally { setGuardando(false); }
  }

  function enviar(e) {
    e.preventDefault();
    if (!puedeGuardar) return;
    if (cambiaCorreo) setConfirmar(true); else guardar();
  }

  const conContactoInactivo = contactoId && !contactos.some(c => c.id === contactoId);

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="editar-titulo"
      style={{position:"fixed", inset:0, zIndex:9997, background:"rgba(15,23,42,0.45)",
        backdropFilter:"blur(3px)", display:"flex", alignItems:"flex-start", justifyContent:"center",
        padding:"24px 16px", overflowY:"auto"}}>
      <form onSubmit={enviar}
        style={{background:C.white, borderRadius:14, width:"100%", maxWidth:520,
          boxShadow:"0 24px 80px #00000020", border:`1px solid ${C.border}`, marginBottom:24}}>
        <div style={{padding:"16px 22px", borderBottom:`1px solid ${C.border}`,
          display:"flex", alignItems:"center", justifyContent:"space-between"}}>
          <div style={{display:"flex", alignItems:"center", gap:10}}>
            <div style={{width:32, height:32, borderRadius:8, background:C.accentL,
              display:"flex", alignItems:"center", justifyContent:"center"}}>
              <Pencil size={15} color={C.accent} aria-hidden="true"/>
            </div>
            <div>
              <h2 id="editar-titulo" style={{margin:0, fontSize:15, fontWeight:700, color:C.text}}>Editar acceso</h2>
              <div style={{fontSize:11, color:C.sub}}>{acceso.proyecto_nombre}</div>
            </div>
          </div>
          <button type="button" onClick={onCerrar} aria-label="Cerrar" disabled={guardando}
            style={{background:"transparent", border:"none", cursor:"pointer", color:C.light, padding:4}}>
            <X size={17}/>
          </button>
        </div>

        <div style={{padding:"18px 22px", display:"flex", flexDirection:"column", gap:14}}>
          <div>
            <label htmlFor="ed-nombre" style={label}>Nombre</label>
            <input id="ed-nombre" value={nombre} maxLength={120} disabled={guardando}
              onChange={e => setNombre(e.target.value)} style={iStyle}/>
            {errNombre && <div style={{fontSize:11, color:C.err, marginTop:4}}>{errNombre}</div>}
          </div>
          <div>
            <label htmlFor="ed-email" style={label}>Correo electrónico</label>
            <input id="ed-email" type="email" value={email} maxLength={254}
              disabled={guardando || !pendiente} onChange={e => setEmail(e.target.value)}
              style={{...iStyle, background: pendiente ? C.white : C.bg}}/>
            {errEmail && <div style={{fontSize:11, color:C.err, marginTop:4}}>{errEmail}</div>}
            {!pendiente && (
              <div style={{fontSize:11, color:C.sub, marginTop:4, lineHeight:1.5}}>
                El correo de un acceso activo no se puede cambiar. Para usar otro correo, revoca este acceso y crea una invitación nueva.
              </div>
            )}
            {cambiaCorreo && !errEmail && (
              <div style={{fontSize:11, color:"#92400e", marginTop:4, lineHeight:1.5}}>
                Se anulará la invitación actual y se enviará una nueva a este correo.
              </div>
            )}
          </div>
          <div>
            <label htmlFor="ed-contacto" style={label}>
              Empresa o contacto asociado <span style={{fontWeight:400, color:C.light}}>(opcional)</span>
            </label>
            <select id="ed-contacto" value={contactoId} onChange={e => setContactoId(e.target.value)}
              disabled={guardando} style={iStyle}>
              <option value="">— Sin asociar —</option>
              {conContactoInactivo && <option value={contactoId}>{acceso.contacto_razon_social || "Contacto actual"}</option>}
              {contactos.map(c => <option key={c.id} value={c.id}>{c.razon_social}{c.rut ? ` · ${c.rut}` : ""}</option>)}
            </select>
          </div>

          {error && (
            <div role="alert" style={{color:C.err, background:C.errL, border:`1px solid ${C.err}40`, borderRadius:8,
              padding:"9px 12px", fontSize:12, lineHeight:1.5, display:"flex", gap:7, alignItems:"flex-start"}}>
              <AlertCircle size={14} style={{flexShrink:0, marginTop:1}}/> {error}
            </div>
          )}

          <div style={{display:"flex", gap:10, justifyContent:"flex-end"}}>
            <button type="button" onClick={onCerrar} disabled={guardando} style={btnSec}>Cancelar</button>
            <button type="submit" disabled={!puedeGuardar}
              style={{display:"flex", alignItems:"center", gap:6, padding:"8px 20px", borderRadius:8,
                border:"none", fontWeight:700, fontSize:14, fontFamily:"'DM Sans',sans-serif",
                background: puedeGuardar ? C.accent : C.light, color:"#fff",
                cursor: puedeGuardar ? "pointer" : "not-allowed"}}>
              {guardando && !confirmar ? <><Loader size={13} className="spinning"/> Guardando…</>
                : <><Save size={13}/> Guardar cambios</>}
            </button>
          </div>
        </div>
      </form>

      {confirmar && (
        <div role="alertdialog" aria-modal="true" aria-labelledby="reemplazar-titulo"
          style={{position:"fixed", inset:0, zIndex:9999, background:"rgba(15,23,42,0.4)",
            display:"flex", alignItems:"center", justifyContent:"center", padding:16}}>
          <div style={{background:C.white, borderRadius:14, padding:"24px 26px", maxWidth:420, width:"100%",
            border:`1px solid ${C.border}`, boxShadow:"0 20px 60px #0000002a"}}>
            <h3 id="reemplazar-titulo" style={{margin:"0 0 8px", fontSize:16}}>¿Reemplazar la invitación?</h3>
            <p style={{margin:"0 0 18px", fontSize:13, color:C.sub, lineHeight:1.6}}>
              El enlace enviado a <strong style={{color:C.text}}>{acceso.email}</strong> dejará de funcionar y se
              enviará una invitación nueva a <strong style={{color:C.text}}>{emailNuevo}</strong>.
            </p>
            <div style={{display:"flex", gap:10, justifyContent:"flex-end"}}>
              <button type="button" onClick={() => setConfirmar(false)} disabled={guardando} style={btnSec}>Volver</button>
              <button type="button" onClick={guardar} disabled={guardando}
                style={{padding:"8px 18px", borderRadius:8, border:"none", background:C.accent, color:"#fff",
                  fontWeight:700, cursor:"pointer", fontFamily:"'DM Sans',sans-serif"}}>
                {guardando ? "Reemplazando…" : "Sí, reemplazar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
